import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router'
import { fetchUser, updateUserTokenDevice } from '../../store/actionsCreators/userActions'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { fetchProperties } from '../../store/actionsCreators/propertyActions'
import { fetchDocuments } from '../../store/actionsCreators/documentActions'
import SearchProvider from '../../app/SearchProvider'
import useOrientation from '../../hooks/UseOrientation'
import LandscapePlaceholder from '../shared/landscape-placeholder'
import { fetchChatRooms, getChatList } from '../../store/actionsCreators/chatListActions'
import { fetchViewings } from '../../store/actionsCreators/viewingsActions'
import { fetchAppConfig } from '../../store/actionsCreators/appActions'
import { fetchAdvertises } from '../../store/actionsCreators/advertiseActions/fetchAdvertiseAction'
import Loader from '../../app/custom/loaders/Loader'
import asyncQueue from '../../functions/utils/asyncQueue'
import { ACCESS_KEY, USER_ID_KEY } from '../../urls'
import _ from 'lodash'
import { landlordSocket } from '../../functions/socket/landlordSocket'
import { store, type AppDispatch, type RootState } from '../../store/store'
import { type NavigateFunction } from 'react-router-dom'
import AlertsComponentVerify from '../../AlertsComponentVerify'
import useLandlordLoadProcessData from "./useLandlordLoadProcessData";
import { fetchOffers } from '../../store/actionsCreators/offerActions/fetchOffers'
import { initializeFirebase } from '../../functions/shared/firebase-token/initializeFirebase'
import { disposeWebSocket, getWebSocketDiagnostics, initializeWebSocket } from '../../functions/socket/initializeWebSocket'
import { checkToken } from '../../functions/shared/firebase-token/checkToken'
import { swalError } from '../../functions/swalOptions'
import useMediaQuery from '../../hooks/useMediaQuery'
import { userSlice } from '../../store/slices/userSlice'
import { unitsSlice } from '../../store/slices/unitsSlice'
import { loadUnitsInBatchesHelper, loadUsersInBatchesHelper } from './adminBatchLoaders'
import { getAdminRole } from '../../config/adminRoles'
const webSocket: WebSocket | null = null;

export default function AdminModule(): JSX.Element {
    let userID = window.localStorage.getItem(USER_ID_KEY)

    const dispatch = useAppDispatch()
    const isUserVerified = useAppSelector((state: RootState) => state.modal.isUserVerified)
    const currentUser = useAppSelector((state: RootState) => state.user.currentUser)
    const adminRole = getAdminRole(currentUser as { email?: string })
    const allUsersFromState = useAppSelector((state: RootState) => state.user.allUsers as any[] | undefined)
    const allUnitsFromState = useAppSelector((state: RootState) => state.units.units as any[] | undefined)
    const [dataLoaded, setDataLoaded] = useState(false)
    const { orientation } = useOrientation()

    const isDesktop = useMediaQuery("(min-width: 992px)");
    const navigate = useNavigate()
    const usersLoadRunRef = React.useRef(0)
    const unitsLoadRunRef = React.useRef(0)
    const usersAbortRef = React.useRef<AbortController | null>(null)
    const unitsAbortRef = React.useRef<AbortController | null>(null)
    const usersSeedRef = React.useRef<any[]>(Array.isArray(allUsersFromState) ? allUsersFromState : [])
    const unitsSeedRef = React.useRef<any[]>(Array.isArray(allUnitsFromState) ? allUnitsFromState : [])
    const usersBatchMetaRef = React.useRef<{ isLoading: boolean; error: string | null; loadedCount: number; done: boolean }>({
        isLoading: false,
        error: null,
        loadedCount: 0,
        done: false,
    })
    const unitsBatchMetaRef = React.useRef<{ isLoading: boolean; error: string | null; loadedCount: number; done: boolean }>({
        isLoading: false,
        error: null,
        loadedCount: 0,
        done: false,
    })

    const USERS_BATCH_LIMIT = 200
    const UNITS_BATCH_LIMIT = 50
    const BATCH_RETRY_ATTEMPTS = 3
    const BATCH_RETRY_BASE_DELAY_MS = 400
    const BATCH_MAX_ITERATIONS = 1000
    const BATCH_STALL_LIMIT = 3

    useEffect(() => {
        usersSeedRef.current = Array.isArray(allUsersFromState) ? allUsersFromState : []
    }, [allUsersFromState])

    useEffect(() => {
        unitsSeedRef.current = Array.isArray(allUnitsFromState) ? allUnitsFromState : []
    }, [allUnitsFromState])

    useEffect(() => {
        return () => {
            usersLoadRunRef.current += 1
            unitsLoadRunRef.current += 1
            usersAbortRef.current?.abort()
            unitsAbortRef.current?.abort()
        }
    }, [])

    const loadUsersInBatches = async (): Promise<{ isSuccessful: boolean; success: boolean; error?: string }> => {
        return await loadUsersInBatchesHelper({
            pageSize: USERS_BATCH_LIMIT,
            runRef: usersLoadRunRef,
            abortRef: usersAbortRef,
            seed: usersSeedRef.current,
            metaRef: usersBatchMetaRef,
            dispatch,
            startActionCreator: userSlice.actions.userFetching,
            successActionCreator: userSlice.actions.allUsersFetchingSuccess,
            errorActionCreator: userSlice.actions.userFetchingError,
            retryAttempts: BATCH_RETRY_ATTEMPTS,
            retryBaseDelayMs: BATCH_RETRY_BASE_DELAY_MS,
            maxIterations: BATCH_MAX_ITERATIONS,
            stallLimit: BATCH_STALL_LIMIT,
        })
    }

    const loadUnitsInBatches = async (): Promise<{ isSuccessful: boolean; success: boolean; error?: string }> => {
        return await loadUnitsInBatchesHelper({
            pageSize: UNITS_BATCH_LIMIT,
            runRef: unitsLoadRunRef,
            abortRef: unitsAbortRef,
            seed: unitsSeedRef.current,
            metaRef: unitsBatchMetaRef,
            dispatch,
            startActionCreator: unitsSlice.actions.unitsFetching,
            successActionCreator: unitsSlice.actions.unitsFetchingSuccess,
            errorActionCreator: unitsSlice.actions.unitsFetchingError,
            retryAttempts: BATCH_RETRY_ATTEMPTS,
            retryBaseDelayMs: BATCH_RETRY_BASE_DELAY_MS,
            maxIterations: BATCH_MAX_ITERATIONS,
            stallLimit: BATCH_STALL_LIMIT,
        })
    }




    useEffect(() => {


        void (async () => {
            const jwt = window.localStorage.getItem(ACCESS_KEY)
            userID = window.localStorage.getItem(USER_ID_KEY)

            if (jwt && userID && adminRole != null) {
                setDataLoaded(false)
                const brokerageLoaders = adminRole === 'brokerage'
                    ? [
                        dispatch(fetchViewings()), dispatch(fetchOffers()), loadUnitsInBatches(), dispatch(fetchDocuments()),
                        loadUsersInBatches(), dispatch(fetchProperties()), dispatch(fetchChatRooms()), dispatch(fetchAdvertises()), dispatch(fetchAppConfig())
                    ]
                    : []
                // App owns the current-user bootstrap. Do not race it with a second request here.
                const results = await asyncQueue(brokerageLoaders)

                const isLoad = results.every((el: any) => {
                    return el?.isSuccessful === true || el?.success === true
                })


                if (isLoad) {
                    setDataLoaded(true)

                    const userAgent = navigator.userAgent

                    const isIPhone = _.includes(userAgent.toLowerCase(), 'iphone')
                    const isAndroid = _.includes(userAgent.toLowerCase(), 'android')


                    if (isAndroid) {
                        void initializeFirebase(webSocket!, dispatch, navigate, landlordSocket)
                    } else if (isIPhone) {
                        await checkToken(dispatch, navigate).then(() => {
                            initializeWebSocket(webSocket, dispatch, navigate, landlordSocket, { caller: 'admin-bootstrap-ios' });
                        })
                    } else {
                        await dispatch(updateUserTokenDevice(`BrowserTokenUnknownToken${userID}`))
                        initializeWebSocket(webSocket, dispatch, navigate, landlordSocket, { caller: 'admin-bootstrap-web' });
                    }
                } else {
                    // A failed bulk request must never leave the whole application on its loader.
                    setDataLoaded(true)
                    if (results.some((el: any) => el?.isSuccessful === false && el?.error === "Unauthorized") || results.some((el: any) => {
                        return el?.isSuccessful === false && (el?.response?.error?.status === 204 || el?.response?.error?.name === "RequestError")
                    })) {
                        const result = await swalError("Error while executing the request")
                        if (result.isConfirmed) {
                            navigate('/auth/login')
                        }
                    } else {
                        const result = await swalError("Error while executing the request")
                        if (result.isConfirmed) {
                            window.location.reload()
                        }
                    }

                }


            } else if (adminRole != null) {
                setDataLoaded(true)
            }
        })()
        const body = document.querySelector('body')
        if (body != null) {
            body.setAttribute('style', 'user-select: text !important;')
        }
    }, [adminRole])

    // useEffect(() => {
    //     if (dataLoaded) {
    //         // логика по загрузке нужных роутов в лэндлорде аналог  AgentLoadProcessData
    //     }
    // }, [dataLoaded])

    useEffect(() => {
        const interval = setInterval(() => {
            const currentTime = new Date().getTime();
            const diagnostics = getWebSocketDiagnostics();
            const timestampDifference = currentTime - diagnostics.lastActivityTimestamp;
            const isSocketUnavailable = diagnostics.readyState !== WebSocket.OPEN && diagnostics.readyState !== WebSocket.CONNECTING;
            if (timestampDifference > 40000 && isSocketUnavailable && !diagnostics.hasReconnectTimer) {
                console.log('[WS watchdog] stale & unavailable socket, forcing reconnect', diagnostics);
                initializeWebSocket(webSocket, dispatch, navigate, landlordSocket, {
                    forceReconnect: true,
                    caller: 'admin-watchdog',
                    reason: 'stale-unavailable-socket'
                });
            }
        }, 20000);
        return () => { clearInterval(interval); };
    }, []);




    useLandlordLoadProcessData(dataLoaded)

    useEffect(() => {
        const repeatConnection = (): void => {
            const brokerageLoaders = adminRole === 'brokerage'
                ? [
                    dispatch(fetchViewings(true)), dispatch(fetchOffers(true)), loadUnitsInBatches(), dispatch(fetchDocuments(true)),
                    loadUsersInBatches(), dispatch(fetchAdvertises(true)), dispatch(fetchProperties()), dispatch(fetchChatRooms()), dispatch(fetchAppConfig())
                ]
                : []
            void asyncQueue(brokerageLoaders)
            initializeWebSocket(webSocket, dispatch, navigate, landlordSocket, { caller: 'open-app-event' });
        }
        window.addEventListener('open-app', repeatConnection)
        return () => {
            window.removeEventListener('open-app', repeatConnection);
        };
    }, [adminRole])

    useEffect(() => {
        return () => {
            disposeWebSocket('admin-module-unmount')
        }
    }, [])



    if (!dataLoaded) {
        return <Loader />
    }

    return (
        <SearchProvider>
            {/* {orientation === 'landscape' && !isDesktop
                ? <LandscapePlaceholder />
                : */}
            <>
                {
                    // TODO implement
                    isUserVerified || currentUser?.emailVerified === true
                        ? null
                        : [
                            // CREATE FIRST UNIT
                            // '/landlord/modal/landlord_create_unit',
                            // '/landlord/modal/landlord_create_unit_owners_passport',
                            // '/landlord/modal/landlord_create_unit_correct_owner_info',
                            // '/landlord/modal/landlord_create_unit_poa',
                            // '/landlord/modal/landlord_create_unit_info',
                            // '/landlord/modal/landlord_create_main_info_first',
                            // '/landlord/modal/landlord_create_main_info_second',
                            // '/landlord/modal/landlord_create_unit_additional_info',
                            // '/landlord/modal/landlord_create_unit_additional_info_outdoor',
                            // '/landlord/modal/landlord_create_unit_additional_info_files',
                            '/landlord/modal/v2/landlord_create_unit_choose_role/-/-',
                            '/landlord/modal/v2/landlord_create_unit_representative_auth_method/-/-',
                            '/landlord/modal/v2/landlord_create_unit_owner_passport/-/-',
                            '/landlord/modal/v2/landlord_create_unit_id_verification/-/-',
                            '/landlord/modal/v2/landlord_create_unit_find_owner/-/-',
                            '/landlord/modal/v2/landlord_create_unit_wait_for_owner',
                            '/landlord/modal/v2/landlord_create_unit_upload_title_deed/-/-',
                            '/landlord/modal/v2/landlord_create_unit_property_info/-/-',
                            '/landlord/modal/v2/landlord_create_unit_unit_info/-/-',
                            '/landlord/modal/v2/landlord_create_unit_unit_features/-/-',
                            '/landlord/modal/v2/landlord_create_unit_users_passport/-/-',
                            '/landlord/modal/v2/landlord_create_unit_verify_user_id/-/-',

                            '/landlord/modal/update_passport_together',
                            '/landlord/modal/update_emirates_back_together',
                            '/landlord/modal/update_emirates_front_together',
                            '/auth/register_scan_emirates'
                        ].some((path) => location.pathname.includes(path)) // 'landlord/modal/update_passport_together') || location.pathname.includes('/landlord/modal/update_emirates_back_together') || location.pathname.includes('/landlord/modal/update_emirates_front_together') || location.pathname.includes('/auth/register_scan_emirates')
                            ? null
                            : null
                    // <AlertsComponentVerify />
                }
                <Outlet />
            </>
            {/* } */}
        </SearchProvider>
    )
}
